import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import { translateKey } from 'src/locales/translate';
import { TextSkeleton } from 'src/sections/@dashboard/warehouse/components/WarehouseDetailDialogSkeleton';
import { VISIT_MODAL_PAPER_MEDIA } from './visitModalLayout';

const TAB_WIDTHS = [95, 215, 85, 152];

function BadgeSkeleton({ width }: { width: number }): JSX.Element {
  return (
    <Skeleton
      variant="rounded"
      width={width}
      height={24}
      sx={{ borderRadius: 0.75 }}
    />
  );
}

/** One of OrganizationVisitModalDetailsMembers' two cards. */
function MemberCardSkeleton(): JSX.Element {
  const theme = useTheme();

  return (
    <Grid
      item
      xs={12}
      lg={5.5}
      sx={{ p: 2, bgcolor: theme.palette.primary.lighter, borderRadius: 2 }}
    >
      <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
        <Skeleton
          variant="circular"
          width={theme.spacing(5)}
          height={theme.spacing(5)}
        />
        <Stack sx={{ gap: 0.5 }}>
          <TextSkeleton variant="body1" width={140} />
          <TextSkeleton variant="body2" width={110} />
        </Stack>
      </Stack>
    </Grid>
  );
}

interface OrganizationVisitModalSkeletonProps {
  onClose: () => void;
}

/**
 * The visit modal's content while its visit is first read, laid out as a
 * completed visit (the common case where the Warehouse links a visit): the
 * title with the date, clinic and badges, the patient and owner cards, the
 * detail tabs, the "Dolegliwości" tab and the footer. "Zamknij" works.
 *
 * ponytail: the "Asystent Vetsi" side panel depends on the visit's status and
 * age, so a visit that gets it widens the modal when it lands. Mirror the
 * panel here if Warehouse visits turn out to be mostly recent ones.
 */
export default function OrganizationVisitModalSkeleton({
  onClose,
}: OrganizationVisitModalSkeletonProps): JSX.Element {
  const theme = useTheme();
  const { isMd } = useResponsiveBreakpoints();
  const isDown500Px = useMediaQuery(theme.breakpoints.down(500));

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        data-testid="visit-modal-skeleton"
      >
        <Stack
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flex: 1,
          }}
        >
          <Stack>
            <TextSkeleton variant="h6" width={150} />
            <TextSkeleton variant="body1" width={200} />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Stack
              sx={{
                flexDirection: isMd ? 'column' : 'row',
                gap: 1,
                justifyContent: isMd ? 'flex-start' : 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <BadgeSkeleton width={90} />
              <BadgeSkeleton width={225} />
            </Stack>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: isMd ? 'flex-start' : 'flex-end',
              }}
            >
              <BadgeSkeleton width={105} />
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          minHeight: 0,
          [VISIT_MODAL_PAPER_MEDIA]: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ flexShrink: 0, px: 3, pt: 3, pb: 2 }}>
          <Grid
            container
            sx={{ gap: { xs: 1, lg: 0 }, alignItems: 'center', mb: 3 }}
          >
            <MemberCardSkeleton />
            <Grid item xs={12} lg>
              <TextSkeleton
                variant="body1"
                width={16}
                sx={{
                  fontSize: { xs: '20px', md: '24px' },
                  textAlign: 'center',
                }}
              />
            </Grid>
            <MemberCardSkeleton />
          </Grid>
        </Box>
        <Box
          sx={{
            px: 3,
            [VISIT_MODAL_PAPER_MEDIA]: {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Stack
            sx={{
              gap: 2,
              [VISIT_MODAL_PAPER_MEDIA]: { flex: '1 0 auto', minHeight: 0 },
            }}
          >
            <Stack
              sx={{
                flexDirection: 'row',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Tabs value={false}>
                {TAB_WIDTHS.map((width) => (
                  <Tab
                    key={width}
                    disabled
                    label={<Skeleton width={width} />}
                    sx={{ fontSize: 16 }}
                  />
                ))}
              </Tabs>
            </Stack>
            <Stack
              sx={{
                minHeight: 250,
                [VISIT_MODAL_PAPER_MEDIA]: { flex: '1 0 auto' },
              }}
            >
              <Stack sx={{ minHeight: 100 }}>
                <TextSkeleton variant="body1" width="100%" />
                <TextSkeleton variant="body1" width="70%" />
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Stack
          sx={{
            flexShrink: 0,
            px: 3,
            py: 2,
            flexDirection: isDown500Px ? 'column' : 'row',
            gap: 1.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            fullWidth={isDown500Px}
            size="medium"
          >
            {translateKey('common.close')}
          </Button>
          <Skeleton
            variant="rounded"
            width={isDown500Px ? '100%' : 160}
            height={36}
          />
        </Stack>
      </DialogContent>
    </>
  );
}
